import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Building2, 
  MapPin, 
  Users, 
  Camera, 
  KeyRound, 
  Euro, 
  Calendar,
  Phone,
  Mail,
  Search,
  Plus,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { sitesAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';

const Sites = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [sites, setSites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newSite, setNewSite] = useState({
    name: '',
    address: '',
    client: '',
    type: 'commercial',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
  });

  // Load sites from API
  const loadSites = async () => {
    try {
      const response = await sitesAPI.getAll();
      setSites(response.data || []);
    } catch (error) {
      console.error('Error loading sites:', error);
      toast({
        title: "Erreur",
        description: getErrorMessage(error, "Impossible de charger les sites"),
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    loadSites();
  }, []);

  const filteredSites = sites.filter(site =>
    site.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    site.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    setNewSite({
      ...newSite,
      [e.target.name]: e.target.value
    });
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await sitesAPI.create(newSite);
      
      setShowModal(false);
      setNewSite({
        name: '',
        address: '',
        client: '',
        type: 'commercial',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
      });
      
      toast({
        title: "Site créé",
        description: "Le site a été ajouté avec succès",
      });
      loadSites(); // Reload sites from API
    } catch (error) {
      toast({
        title: "Erreur",
        description: getErrorMessage(error, "Impossible de créer le site"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const siteTypes = {
    commercial: 'Commercial',
    industriel: 'Industriel',
    residentiel: 'Résidentiel',
    public: 'Public'
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sites de surveillance</h1>
          <p className="text-gray-500 mt-1">Gérez tous vos sites protégés</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Nouveau site</span>
        </Button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <Input
          placeholder="Rechercher un site..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Sites Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSites.map((site) => (
          <Card key={site.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <Building2 className="w-8 h-8 text-blue-600" />
                  <div>
                    <CardTitle className="text-lg">{site.name}</CardTitle>
                    <Badge variant="secondary" className="mt-1">
                      {siteTypes[site.type] || site.type}
                    </Badge>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-600">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                  <span className="text-sm text-gray-600">{site.address}</span>
                </div>
                
                {site.contactName && (
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{site.contactName}</span>
                  </div>
                )}

                {site.contactPhone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{site.contactPhone}</span>
                  </div>
                )}

                {site.contactEmail && (
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{site.contactEmail}</span>
                  </div>
                )}

                <div className="pt-3 border-t flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center space-x-1">
                      <Camera className="w-4 h-4" />
                      <span>{site.camerasCount || 0}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <KeyRound className="w-4 h-4" />
                      <span>{site.alarmsCount || 0}</span>
                    </span>
                  </div>
                  <Badge variant={site.status === 'active' ? 'success' : 'secondary'}>
                    {site.status === 'active' ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>

                <div className="pt-2 flex space-x-2">
                  <Button variant="outline" size="sm" className="flex-1">
                    <Eye className="w-4 h-4 mr-1" />
                    Voir
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1">
                    <Edit className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Site Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Nouveau site</CardTitle>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <CardDescription>Ajoutez un nouveau site à surveiller</CardDescription>
            </CardHeader>
            <form onSubmit={handleAddSite}>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom du site *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={newSite.name}
                      onChange={handleInputChange}
                      placeholder="Centre commercial XYZ"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client">Client</Label>
                    <Input
                      id="client"
                      name="client"
                      value={newSite.client}
                      onChange={handleInputChange}
                      placeholder="Nom du client"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="type">Type de site *</Label>
                    <select
                      id="type"
                      name="type"
                      value={newSite.type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="commercial">Commercial</option>
                      <option value="industriel">Industriel</option>
                      <option value="residentiel">Résidentiel</option>
                      <option value="public">Public</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Adresse *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={newSite.address}
                    onChange={handleInputChange}
                    placeholder="123 Rue de la Paix, 75001 Paris"
                    required
                  />
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Contact sur site</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="contactName">Nom du contact</Label>
                      <Input
                        id="contactName"
                        name="contactName"
                        value={newSite.contactName}
                        onChange={handleInputChange}
                        placeholder="Jean Dupont"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contactPhone">Téléphone</Label>
                        <Input
                          id="contactPhone"
                          name="contactPhone"
                          value={newSite.contactPhone}
                          onChange={handleInputChange}
                          placeholder="+33 1 23 45 67 89"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contactEmail">Email</Label>
                        <Input
                          id="contactEmail"
                          name="contactEmail"
                          type="email"
                          value={newSite.contactEmail}
                          onChange={handleInputChange}
                          placeholder="contact@site.fr"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
              <div className="flex justify-end space-x-2 p-6 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Création...' : 'Créer le site'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {filteredSites.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Aucun site trouvé</p>
        </div>
      )}
    </div>
  );
};

export default Sites;